/**
 * Rep "signed in" UI hint + the global session-expiry handler (PP-8.1).
 *
 * This is NOT the credential — the real session lives in the BFF's HttpOnly
 * cookie, which JS can never read or write. The sessionStorage flag below is
 * purely a UX shortcut ("should I show the sign-in screen, or try an
 * authenticated call first") — losing/tampering with it can at worst cause a
 * brief wrong-screen flash (frontend-security-auditor, swarm review:
 * confirmed this has no authorization weight, since every real call still
 * goes through the cookie regardless of this flag's value). Deliberately
 * `sessionStorage`, not `localStorage` — a shared/public device should not
 * retain this hint across browser restarts (state-management-critic, swarm
 * review).
 */

const HINT_KEY = 'qfly_portal_signed_in_hint';

export const markSignedInHint = (): void => {
  try {
    sessionStorage.setItem(HINT_KEY, '1');
  } catch {
    /* private mode / storage disabled -- the hint is best-effort only */
  }
};

export const clearSignedInHint = (): void => {
  try {
    sessionStorage.removeItem(HINT_KEY);
  } catch {
    /* ignore */
  }
};

export const hasSignedInHint = (): boolean => {
  try {
    return sessionStorage.getItem(HINT_KEY) === '1';
  } catch {
    return false;
  }
};

/** Registered by `PortalApp.tsx` (holds the router's own `navigate`). */
let onSessionExpired: (() => void) | null = null;

export const setSessionExpiredHandler = (handler: (() => void) | null): void => {
  onSessionExpired = handler;
};

/**
 * Idempotency guard (state-management-critic, swarm review): several
 * independent queries/mutations in flight at once (e.g. the campaigns list
 * + the deals list, both loaded on the same screen) can each independently
 * receive a `session_expired` 401 around the same moment — without this
 * guard, each would redundantly clear the hint and re-navigate. Reset only
 * on a fresh, confirmed sign-in (`resetSessionExpiredGuard`), never on a
 * timer — there is nothing stale about a guard that correctly stays "already
 * handled" until the rep actually signs in again.
 */
let alreadyHandled = false;

export const resetSessionExpiredGuard = (): void => {
  alreadyHandled = false;
};

/** Call on every `code === "session_expired"` 401 — never on a wrong-OTP-code
 * 401 (`code === "invalid_code"`/`"not_eligible"`), which has nothing to do
 * with an established session expiring. */
export const handleSessionExpired = (): void => {
  if (alreadyHandled) return;
  alreadyHandled = true;
  clearSignedInHint();
  onSessionExpired?.();
};
